import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Microsoft OAuth Configuration
const MS_CLIENT_ID = process.env.REACT_APP_MS_CLIENT_ID || '';
const MS_TENANT_ID = process.env.REACT_APP_MS_TENANT_ID || 'common';
const MS_REDIRECT_URI = `${window.location.origin}/auth/microsoft/callback`;
const MS_AUTHORITY = `https://login.microsoftonline.com/${MS_TENANT_ID}`;

const AuthContext = createContext(null);

// Token storage helper. Reads from sessionStorage first (used by the
// "Preview as prospect" tab so it doesn't clobber the admin's own session)
// and falls back to localStorage for regular logins.
const getStoredToken = () => {
  try {
    const sess = sessionStorage.getItem('qindex_token');
    if (sess) return sess;
  } catch { /* ignore — private mode etc. */ }
  return localStorage.getItem('qindex_token');
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState(null); // 'jwt', 'google', or 'microsoft'

  useEffect(() => {
    const initAuth = async () => {
      // Skip auth bootstrap entirely on public marketing/prospect routes — they don't need a user
      // and firing /api/auth/* always 401s, slowing first paint by 200-500ms.
      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        const isPublicRoute = (
          path === '/' ||
          path.startsWith('/pitch/') ||
          path.startsWith('/demo-access/') ||
          path === '/demo-preview' ||
          path === '/portal/login' ||
          path === '/contact' ||
          path.startsWith('/access/')   // magic-link invite landing
        );
        if (isPublicRoute && !getStoredToken()) {
          setLoading(false);
          return;
        }
      }

      // FAST PATH: if we have a JWT token, use it immediately
      const savedToken = getStoredToken();
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` },
            timeout: 10000,
          });
          setUser(response.data);
          setOrganisation(response.data.organisation);
          setToken(savedToken);
          setAuthMethod('jwt');
          setLoading(false);
          return;
        } catch (error) {
          // Only clear token on explicit 401/403. On other errors, fall through to session path.
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            // Clear whichever store holds the bad token
            try { sessionStorage.removeItem('qindex_token'); } catch { /* ignore */ }
            localStorage.removeItem('qindex_token');
            setToken(null);
          }
          // For network / timeout / aborted errors: don't remove token — user can retry
        }
      }

      // SLOW PATH: no JWT — try Google session (cookie-based)
      try {
        const sessionResponse = await axios.get(`${API}/auth/session/me`, {
          withCredentials: true,
          timeout: 5000,
        });
        if (sessionResponse.data) {
          setUser(sessionResponse.data);
          setOrganisation(sessionResponse.data.organisation);
          setAuthMethod('google');
        }
      } catch (error) {
        // No session either — anonymous
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // Traditional email/password login (JWT)
  const login = async (email, password, rememberDevice = false) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { 
        email, 
        password,
        remember_device: rememberDevice 
      });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return { 
          success: false, 
          requires_2fa: true,
          temp_token: response.data.temp_token
        };
      }
      
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('qindex_token', newToken);
      setToken(newToken);
      setUser(userData);
      setOrganisation(userData.organisation);
      setAuthMethod('jwt');
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  // Verify 2FA code
  const verify2FA = async (code, tempToken) => {
    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, {
        code: code,
        temp_token: tempToken
      });
      
      const { token: newToken, user: userData, warning } = response.data;
      localStorage.setItem('qindex_token', newToken);
      setToken(newToken);
      setUser(userData);
      setOrganisation(userData.organisation);
      setAuthMethod('jwt');
      return { success: true, warning };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Invalid 2FA code'
      };
    }
  };

  // Google OAuth login - redirect to Emergent Auth
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Microsoft OAuth login - redirect to Microsoft login
  const loginWithMicrosoft = () => {
    // Store return URL for after auth
    sessionStorage.setItem('ms_auth_return_url', window.location.origin + '/app');
    
    // Build Microsoft OAuth URL
    const params = new URLSearchParams({
      client_id: MS_CLIENT_ID,
      response_type: 'token',
      redirect_uri: MS_REDIRECT_URI,
      scope: 'openid profile email User.Read',
      response_mode: 'fragment'
    });
    
    window.location.href = `${MS_AUTHORITY}/oauth2/v2.0/authorize?${params.toString()}`;
  };

  // Handle Microsoft OAuth callback
  const handleMicrosoftCallback = async (accessToken) => {
    try {
      const response = await axios.post(`${API}/auth/microsoft/login`, {
        access_token: accessToken
      });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return { 
          success: false, 
          requires_2fa: true,
          temp_token: response.data.temp_token
        };
      }
      
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('qindex_token', newToken);
      setToken(newToken);
      setUser(userData);
      setOrganisation(userData.organisation);
      setAuthMethod('microsoft');
      return { success: true, is_new_user: response.data.is_new_user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Microsoft login failed'
      };
    }
  };

  // Set user from Google OAuth callback
  const setGoogleUser = (userData) => {
    setUser(userData);
    setOrganisation(userData.organisation);
    setAuthMethod('google');
  };

  const logout = async () => {
    // Clear session-based auth (Google)
    if (authMethod === 'google') {
      try {
        await axios.post(`${API}/auth/session/logout`, {}, { withCredentials: true });
      } catch (error) {
        console.error('Session logout error:', error);
      }
    }
    
    // Clear JWT-based auth (both stores — sessionStorage is used by preview tabs)
    try { sessionStorage.removeItem('qindex_token'); } catch { /* ignore */ }
    localStorage.removeItem('qindex_token');
    sessionStorage.removeItem('qindex_sfo_initial_redirect');
    setToken(null);
    setUser(null);
    setOrganisation(null);
    setAuthMethod(null);
  };

  /**
   * Login with an existing token (for external authentication flow)
   * Used when redirected from main website with a pre-validated token
   */
  const loginWithToken = async (newToken, userData) => {
    localStorage.setItem('qindex_token', newToken);
    setToken(newToken);
    setUser(userData);
    setAuthMethod('jwt');
    // Fetch full user details including organisation
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setOrganisation(response.data.organisation);
    } catch (error) {
      console.error('Failed to fetch organisation details:', error);
    }
  };

  const getAuthHeader = () => {
    if (authMethod === 'google') {
      return {}; // Google auth uses cookies
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Check if user has super admin role
  const isSuperAdmin = () => user?.role === 'super_admin';
  
  // Check if user has org admin or super admin role
  const isOrgAdmin = () => ['super_admin', 'org_admin', 'admin', 'owner', 'practice_manager'].includes(user?.role);

  // 2FA Management
  const setup2FA = async () => {
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to setup 2FA' 
      };
    }
  };

  const enable2FA = async (code) => {
    try {
      const response = await axios.post(`${API}/auth/2fa/enable`, { code }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update user state
      if (user) {
        setUser({ ...user, two_factor_enabled: true });
      }
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to enable 2FA' 
      };
    }
  };

  const disable2FA = async (code) => {
    try {
      const response = await axios.post(`${API}/auth/2fa/disable`, { code }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update user state
      if (user) {
        setUser({ ...user, two_factor_enabled: false });
      }
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to disable 2FA' 
      };
    }
  };

  const get2FAStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, enabled: response.data.two_factor_enabled };
    } catch (error) {
      return { success: false, enabled: false };
    }
  };

  // Password Reset
  const requestPasswordReset = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/password-reset/request`, { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to request password reset' 
      };
    }
  };

  const confirmPasswordReset = async (resetToken, newPassword) => {
    try {
      const response = await axios.post(`${API}/auth/password-reset/confirm`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to reset password' 
      };
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      organisation, 
      token, 
      loading, 
      authMethod,
      login,
      verify2FA,
      loginWithGoogle,
      loginWithMicrosoft,
      handleMicrosoftCallback,
      setGoogleUser,
      logout, 
      loginWithToken, 
      getAuthHeader,
      isSuperAdmin,
      isOrgAdmin,
      // 2FA functions
      setup2FA,
      enable2FA,
      disable2FA,
      get2FAStatus,
      // Password reset
      requestPasswordReset,
      confirmPasswordReset
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
